import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Modal, Offcanvas } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { IntegrationTypeIcon } from '../../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { useIsNarrowScreen } from '../../promote/work/useIsNarrowScreen';
import { FindItResult } from '../findItSearch';
import { isSearchableService, PanelService } from '../servicesPanelServices';
import { findItMessages as m } from '../servicesPanelMessages';
import { useFindItSearch } from './useFindItSearch';
import { FindItResults } from './FindItResults';
import { FindItPaste } from './FindItPaste';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for FindIt; each part uses its own slice of it */
import styles from './FindIt.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FindItSheetProps {
  service: PanelService;
  song: { title: string; artistName: string };
  /** Saves the address as the link of the service; true when it saved. */
  onSave: (url: string) => Promise<boolean>;
  onPasteInstead: () => void;
  onClose: () => void;
}

/**
 * "Find it" (design d4): search one service and use a result as its link. A dialog, a bottom sheet
 * below 700 px (d7). It never changes the song itself: only the link.
 */
const FindItSheetComponent: React.FC<FindItSheetProps> = ({ service, song, onSave, onPasteInstead, onClose }) => {
  const intl = useIntl();
  const isNarrow = useIsNarrowScreen();
  const searchable = isSearchableService(service.integrationType);
  const search = useFindItSearch({ integrationType: service.integrationType, song, enabled: searchable });
  const [savingUrl, setSavingUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const { name } = service;

  const save = useCallback(
    (url: string) => {
      setFailedUrl(null);
      setSavingUrl(url);
      onSave(url)
        .then((saved) => {
          setSavingUrl(null);
          if (saved) {
            onClose();
          } else {
            setFailedUrl(url);
          }
        })
        .catch(console.error);
    },
    [onClose, onSave]
  );
  const pick = useCallback((result: FindItResult) => save(result.url), [save]);

  const content = (
    <div data-testid="find-it-sheet">
      <div className={styles.head}>
        <IntegrationTypeIcon integrationType={service.integrationType} size={30} />
        <div className={styles.headText}>
          <b id="find-it-title">{intl.formatMessage(m.title, { title: song.title, service: name })}</b>
          <small>{withBrandWord(intl.formatMessage(m.subtitle, { service: name }))}</small>
        </div>
        <button type="button" className="btn-close" aria-label={intl.formatMessage(m.close)} onClick={onClose} />
      </div>
      {searchable && (
        <div className={styles.search}>
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
            <Input
              autoFocus
              value={search.query}
              aria-label={intl.formatMessage(m.searchLabel, { service: name })}
              onChange={(event) => search.setQuery(event.target.value)}
            />
          </div>
        </div>
      )}
      {searchable ? (
        <FindItResults
          serviceName={name}
          status={search.status}
          results={search.results}
          savingUrl={savingUrl}
          failedUrl={failedUrl}
          onPick={pick}
          onRetry={search.retry}
        />
      ) : (
        <FindItPaste
          integrationType={service.integrationType}
          serviceName={name}
          isSaving={savingUrl !== null}
          saveFailed={failedUrl !== null}
          onSave={save}
        />
      )}
      {searchable && (
        <div className={styles.foot}>
          <span className={styles.grow}>
            {search.status === 'loading' ? intl.formatMessage(m.searching, { service: name }) : null}
            {search.status === 'done' ? intl.formatMessage(m.from, { service: name }) : null}
          </span>
          <button type="button" className={styles.link} onClick={onPasteInstead}>
            {intl.formatMessage(m.pasteInstead)}
          </button>
        </div>
      )}
    </div>
  );

  return isNarrow ? (
    <Offcanvas isOpen toggle={onClose} direction="bottom" aria-labelledby="find-it-title">
      {content}
    </Offcanvas>
  ) : (
    <Modal isOpen toggle={onClose} centered aria-labelledby="find-it-title">
      {content}
    </Modal>
  );
};

export const FindItSheet = TypedMemo(FindItSheetComponent);
